/**
 * Session expiry: detect a dead session, sign out ONCE, and send the user to /login.
 *
 * Incident 2026-10-03 15:16 UTC (PROD, Safari, a manager on /events/:id):
 *   - `GET /auth/v1/user` → 403 `session_not_found`. auth-js maps that to AuthSessionMissingError
 *     and removes the session from storage while it holds its lock.
 *   - ~20 event-page requests were queued behind that lock. When it released they found no
 *     session, so supabase-js sent them with the ANON key. RLS on those tables calls helpers
 *     anon cannot execute → "permission denied for function has_org_access / is_internal_staff",
 *     and the user saw a half-loaded page.
 *
 * This module is deliberately PURE (no supabase import) so the rules can be unit-tested and so
 * services/supabase.ts can use it without an import cycle. supabase.ts builds the singleton
 * with `createSessionExpiryHandler({ signOut: signOutCompletely, ... })`.
 *
 * Rules:
 *   1. Only a PROTECTED page reacts. Auth pages (/setup-account, /verify-invitation,
 *      /reset-password, ...) run their own session flow; public pages (/redeem, /self-checkin,
 *      /lp/..., ...) are meant to run logged out. Neither is signed out or redirected.
 *   2. Single flight: many requests fail at once, but there is one sign-out and one navigation.
 *   3. From the moment expiry is detected, data requests (/rest, /functions, /storage) are
 *      refused locally instead of going out as anon (see `shouldBlockRequest`). A data
 *      request a protected page is about to send WITH THE ANON KEY is itself proof the
 *      session is gone: it is refused and starts handling. That covers the case where the
 *      token vanished without any auth response for us to observe (another tab, a cached
 *      profile rendering the page before the session is checked).
 *   4. Sign out with signOutCompletely() FIRST, navigate AFTER (see
 *      fix_logout_left_the_token_behind): navigating first aborts the sign-out.
 */

export type RouteKind = "auth" | "public" | "protected";

/**
 * Pages that own their own session flow. Never signed out or redirected from here, and
 * AuthContext skips its session initialisation on them.
 */
export const AUTH_ROUTE_PREFIXES: readonly string[] = [
  "/login",
  // NOT "/oauth/consent": it needs AuthContext to load the signed-in user (it reads
  // useAuth().isAuthenticated), so it is treated as protected. On expiry it goes to /login
  // with returnTo, which brings the user back to the consent screen after sign-in.
  "/forgot-password",
  "/reset-password",
  "/setup-account",
  "/confirm-reset",
  "/verify-invitation",
];

/**
 * Pages designed to run logged out (top-level routes in router.tsx outside RequireAuth).
 * Matched on a path-SEGMENT boundary, so "/register" covers "/register/:id" but not a
 * hypothetical "/registrations".
 */
export const PUBLIC_ROUTE_PREFIXES: readonly string[] = [
  "/redeem",
  "/voucher-locations",
  "/email-preferences",
  "/commission-calculator-public",
  "/commission-estimator-public",
  "/checkin",
  "/eventcheckin",
  "/event-register",
  "/view",
  "/register",
  "/guest-confirm",
  "/event-self-checkin",
  "/self-checkin",
  "/t",
  "/refer",
  "/qrcode",
  "/lp",
];

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(prefix + "/");
}

/**
 * Everything not explicitly auth or public is protected (it sits behind RequireAuth).
 *
 * Case-INSENSITIVE, to match React Router: no route sets `caseSensitive`, so `/Redeem` and
 * `/SELF-CHECKIN` render the public page. Classifying them protected sent a logged-out visitor
 * on a mixed-case link to /login (audit SE-1, 2026-10-03). Only the prefix match is lowercased;
 * the URL itself (and any case-sensitive :param such as a token code) is never rewritten.
 */
export function classifyPath(pathname: string): RouteKind {
  const p = ((pathname || "/").replace(/\/+$/, "") || "/").toLowerCase();
  if (AUTH_ROUTE_PREFIXES.some((x) => matchesPrefix(p, x))) {
    return "auth";
  }
  if (PUBLIC_ROUTE_PREFIXES.some((x) => matchesPrefix(p, x))) {
    return "public";
  }
  return "protected";
}

/**
 * True for the login page itself (`/login`, `/Login`, `/LOGIN/`), using the same
 * normalisation as classifyPath. React Router matches routes case-insensitively, so `/Login`
 * renders the login form; an exact `=== "/login"` check skipped the "already signed in →
 * send into the app" branch for it (2026-10-03, consistent with SE-1).
 */
export function isLoginPath(pathname: string): boolean {
  const p = ((pathname || "/").replace(/\/+$/, "") || "/").toLowerCase();
  return p === "/login";
}

/** GoTrue error codes that mean the session or refresh token is gone for good. */
export const SESSION_INVALID_CODES: ReadonlySet<string> = new Set([
  "session_not_found",
  "session_expired",
  "refresh_token_not_found",
  "refresh_token_already_used",
  "invalid_refresh_token",
  "bad_jwt",
]);

interface MaybeAuthError {
  name?: string;
  code?: string;
  status?: number;
  message?: string;
  __isAuthError?: boolean;
}

/**
 * True only for a DEFINITIVE "this session is dead" auth error. PostgREST errors (403
 * permission denied, 42501, etc.) are not auth-js errors and never match — a broad match here
 * is what caused the 2026-05-19 mass-logout incident.
 */
export function isSessionInvalidError(error: unknown): boolean {
  const err = (error ?? {}) as MaybeAuthError;
  if (
    err.name === "AuthSessionMissingError" ||
    err.message === "Auth session missing!"
  ) {
    return true;
  }
  if (typeof err.code === "string" && SESSION_INVALID_CODES.has(err.code)) {
    return true;
  }
  return (
    err.__isAuthError === true && (err.status === 401 || err.status === 403)
  );
}

/**
 * True when an HTTP response from the auth server proves the session is dead:
 *   - GET  /auth/v1/user                          → 401 or 403
 *   - POST /auth/v1/token?grant_type=refresh_token → 400, 401 or 403
 * A failed PASSWORD grant (wrong password on /login) is a 400 too and must NOT match.
 */
export function isSessionInvalidResponse(
  url: string,
  method: string,
  status: number,
): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url, "http://localhost");
  } catch {
    return false;
  }
  const m = (method || "GET").toUpperCase();
  if (parsed.pathname.endsWith("/auth/v1/user")) {
    return m === "GET" && (status === 401 || status === 403);
  }
  if (parsed.pathname.endsWith("/auth/v1/token")) {
    return (
      parsed.searchParams.get("grant_type") === "refresh_token" &&
      (status === 400 || status === 401 || status === 403)
    );
  }
  return false;
}

const DATA_PATH_MARKERS = ["/rest/v1/", "/functions/v1/", "/storage/v1/"];

export function isDataRequest(url: string): boolean {
  return DATA_PATH_MARKERS.some((m) => url.includes(m));
}

export const SESSION_EXPIRED_MESSAGE =
  "Your session expired. Please sign in again.";

export interface SessionExpiryDeps {
  /** Must be signOutCompletely(): finishes before returning, clears the token on failure. */
  signOut: () => Promise<void>;
  /** Full-page navigation (window.location.replace in the app). */
  navigate: (url: string) => void;
  getPathname: () => string;
  getSearch: () => string;
  /** Persist the requested URL so LoginForm can return there (sessionStorage 'returnTo'). */
  saveReturnTo: (path: string) => void;
  /** Forensic crumb, same shape as AuthContext's usls_last_logout_reason. */
  recordReason: (source: string, extra: Record<string, unknown>) => void;
  /**
   * The Authorization header supabase-js sends when it has NO session
   * (`Bearer <anon key>`). Seeing it on a data request from a protected page means the
   * session is already gone.
   */
  anonAuthorization: string;
}

export interface SessionExpiryHandler {
  /**
   * Called on any definitive session-invalid signal. Returns the single in-flight promise,
   * or undefined when the current page is not protected (nothing done).
   */
  handle: (source: string) => Promise<void> | undefined;
  /** True once expiry handling has started on this page (data requests are being refused). */
  isExpired: () => boolean;
  /** True while/after ANY sign-out we started is running; the starter owns navigation. */
  isSigningOut: () => boolean;
  /** A deliberate sign-out (logout/forceLogout) began; ignore the SIGNED_OUT it emits. */
  noteDeliberateSignOut: () => void;
  /** A new session exists (SIGNED_IN): reset all state, unless a sign-out is in flight. */
  reset: () => void;
  /**
   * fetch-wrapper hook: refuse a data request once expiry is detected, or when a protected
   * page is about to send one with the anon key (which itself starts expiry handling).
   */
  shouldBlockRequest: (url: string, authorization: string | null) => boolean;
  /** fetch-wrapper hook: inspect an auth response and trigger handling if it proves expiry. */
  observeResponse: (url: string, method: string, status: number) => void;
}

export function createSessionExpiryHandler(
  deps: SessionExpiryDeps,
): SessionExpiryHandler {
  let inFlight: Promise<void> | null = null;
  let expired = false;
  let deliberate = false;

  const handle = (source: string): Promise<void> | undefined => {
    if (inFlight) {
      return inFlight;
    }
    if (deliberate) {
      // logout()/forceLogout() is already signing out and owns the redirect.
      return undefined;
    }
    const pathname = deps.getPathname();
    const kind = classifyPath(pathname);
    if (kind !== "protected") {
      return undefined;
    }

    // Set synchronously, BEFORE any await: requests queued behind auth-js's lock resume as
    // soon as it releases, and they must already see the flag.
    expired = true;

    inFlight = (async () => {
      try {
        deps.recordReason(source, {
          reason: SESSION_EXPIRED_MESSAGE,
          path: pathname,
        });
      } catch {
        // non-critical
      }
      try {
        const returnTo = pathname + deps.getSearch();
        if (returnTo.startsWith("/") && !returnTo.startsWith("//")) {
          deps.saveReturnTo(returnTo);
        }
      } catch {
        // sessionStorage blocked — the user just lands on their role default
      }

      await deps.signOut();

      deps.navigate(
        `/login?expired=true&reason=${encodeURIComponent(SESSION_EXPIRED_MESSAGE)}`,
      );
    })();
    return inFlight;
  };

  return {
    handle,
    isExpired: () => expired,
    isSigningOut: () => inFlight !== null || deliberate,
    noteDeliberateSignOut: () => {
      deliberate = true;
    },
    reset: () => {
      if (inFlight) {
        // Mid sign-out: a SIGNED_IN now (e.g. relayed from another tab) must not reopen the
        // gate for this page's anon requests. The page is about to navigate anyway.
        return;
      }
      inFlight = null;
      expired = false;
      deliberate = false;
    },
    shouldBlockRequest: (url: string, authorization: string | null) => {
      if (!isDataRequest(url)) {
        return false;
      }
      if (expired) {
        return true;
      }
      if (authorization && authorization === deps.anonAuthorization) {
        // A protected page with no session. On auth/public pages handle() does nothing and
        // returns undefined, so their anon requests go out exactly as before.
        return handle("anon-data-request-on-protected-page") !== undefined;
      }
      return false;
    },
    observeResponse: (url: string, method: string, status: number) => {
      if (isSessionInvalidResponse(url, method, status)) {
        // Fire and forget: we are inside auth-js's fetch while it holds its lock, and
        // signOut() needs that lock — awaiting here would deadlock.
        void handle(`auth-response-${status}`);
      }
    },
  };
}
